import { c } from 'ttag';

import { refusalCode } from '../../graphql/refusalCode';
import { Refusal } from '../accounts/portalAccess';

/**
 * Reading of the refusals of `grantDashboard` and `revokeDashboard`. Both directions share this
 * file, acting on the same `dashboard_membership` row, and part where the gestures part. Notes:
 * - MISSING_PERMISSION here is `manage_users`, not `manage_accounts`: granting to a person is
 *   user administration, distinct from declaring the entry in the account's catalogue.
 * - DASHBOARD_NOT_ON_ACCOUNT is the crossing of two accounts: two accounts may declare an entry
 *   with the same label, so the sentence names the account.
 * - DASHBOARD_ARCHIVED cannot happen on a withdrawal, deliberately: withdrawal accepts an
 *   archived entry so it stays removable. Do not align one direction on the other's refusals.
 */
export type DashboardDirection = 'grant' | 'revoke';

export const dashboardRefusalOf = (error: unknown, direction: DashboardDirection): Refusal => {
    const granting = direction === 'grant';

    switch (refusalCode(error)) {
        case 'NOT_AUTHENTICATED':
            return {
                title: c('Dashboard habilitation refusal').t`Your session no longer carries an active membership.`,
                guidance: c('Dashboard habilitation refusal')
                    .t`Sign in again, then come back to this sheet. Nothing was changed.`,
            };
        case 'MISSING_PERMISSION':
            return {
                title: c('Dashboard habilitation refusal').t`Your role does not let you administer users.`,
                guidance: c('Dashboard habilitation refusal')
                    .t`A dashboard is granted person by person, which is an act of user administration — not the same right as declaring the entry in the account's catalogue. Being able to declare an entry says nothing about being able to grant it. Ask for the user-management permission, or hand the gesture to whoever administers this account.`,
            };
        case 'FORBIDDEN_OUT_OF_SCOPE':
            return {
                title: c('Dashboard habilitation refusal').t`This account is outside your portfolio.`,
                guidance: c('Dashboard habilitation refusal')
                    .t`The habilitation hangs from a membership, so it hangs from that membership's account — and acting on it is reserved to whoever carries the account. Ask for it to be added to your portfolio, or hand the gesture over.`,
            };
        case 'MEMBERSHIP_NOT_FOUND':
            return {
                title: c('Dashboard habilitation refusal').t`This membership no longer exists.`,
                guidance: c('Dashboard habilitation refusal')
                    .t`It has disappeared, or left your perimeter, since this sheet was loaded. Reload it: what you are reading is stale.`,
            };
        case 'DASHBOARD_NOT_FOUND':
            return {
                title: c('Dashboard habilitation refusal').t`This catalogue entry no longer exists.`,
                guidance: c('Dashboard habilitation refusal')
                    .t`It has disappeared since this sheet was loaded. Reload it to read the catalogue as it is now.`,
            };
        case 'DASHBOARD_NOT_ON_ACCOUNT':
            return granting
                ? {
                      title: c('Dashboard habilitation refusal')
                          .t`This entry does not belong to the account of this membership.`,
                      guidance: c('Dashboard habilitation refusal')
                          .t`A dashboard is the object of one account, and an habilitation hangs from a membership: an entry can only be granted on the account whose object it is. Two accounts may well carry an entry with the same label — the label does not tell them apart, the account does. Grant it from the block of the account it belongs to.`,
                  }
                : {
                      title: c('Dashboard habilitation refusal')
                          .t`This entry is no longer filed under the account of this membership.`,
                      guidance: c('Dashboard habilitation refusal')
                          .t`The withdrawal was refused rather than applied to something else: nothing was removed. Reload the sheet, then withdraw the entry from the account block where it is actually held.`,
                  };
        case 'DASHBOARD_ARCHIVED':
            // Grant only: withdrawal accepts an archived entry so a line on it stays removable.
            return {
                title: c('Dashboard habilitation refusal').t`This entry has left the account's catalogue.`,
                guidance: c('Dashboard habilitation refusal')
                    .t`It can no longer be granted to anybody: the habilitation would be written knowing already that it opens nothing. Clicking again will refuse again, and the three ways of granting a dashboard all answer this the same. Declare the dashboard again on this account, then grant the new entry.`,
            };
        default:
            return granting
                ? {
                      title: c('Dashboard habilitation refusal').t`The dashboard was not granted.`,
                      guidance: c('Dashboard habilitation refusal')
                          .t`This refusal is not one the screen knows how to read. Nothing was written: this person holds exactly what they held. The server's answer is below — it is what the support desk needs.`,
                      detail: error instanceof Error ? error.message : String(error),
                  }
                : {
                      title: c('Dashboard habilitation refusal').t`The dashboard was not withdrawn.`,
                      guidance: c('Dashboard habilitation refusal')
                          .t`This refusal is not one the screen knows how to read. Nothing was deleted: this person still holds it. The server's answer is below — it is what the support desk needs.`,
                      detail: error instanceof Error ? error.message : String(error),
                  };
    }
};
